import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import {
  Alert,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { getPublicUrl } from "../../../utils/Utils";
import { formatMoney, initials, langCodes, specialtyLabel } from "./soHelpers";

const SoExperts = () => {
  const history = useHistory();
  const location = useLocation();
  const assignCaseId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("assign") || "";
  }, [location.search]);

  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [specFilter, setSpecFilter] = useState("");
  const [langFilter, setLangFilter] = useState("");
  const [sort, setSort] = useState("availability");
  const [assignBusy, setAssignBusy] = useState(false);
  const [pickId, setPickId] = useState("");

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const res = await http.get("/api/v1/admin/second-opinion/practitioners?enabled=true", {
        signal,
        suppressInlineErrorSwal: true,
      });
      setExperts(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err?.response?.data?.error || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const specialties = useMemo(() => {
    const set = new Set();
    experts.forEach((e) => {
      (e.specialties || []).forEach((s) => {
        const label = s && typeof s === "object" ? s.label : String(s);
        if (label) set.add(label);
      });
    });
    return [...set].sort();
  }, [experts]);

  const languages = useMemo(() => {
    const set = new Set();
    experts.forEach((e) => langCodes(e).forEach((c) => set.add(c)));
    return [...set].sort();
  }, [experts]);

  const filtered = useMemo(() => {
    let list = experts.filter((e) => {
      if (e.secondOpinionFee == null || !Number.isFinite(Number(e.secondOpinionFee))) return false;
      const needle = q.trim().toLowerCase();
      if (needle && !(e.displayName || "").toLowerCase().includes(needle)) return false;
      if (specFilter) {
        const specs = (e.specialties || [])
          .map((s) => (s && typeof s === "object" ? s.label : String(s)))
          .filter(Boolean);
        if (!specs.includes(specFilter)) return false;
      }
      if (langFilter && !langCodes(e).includes(langFilter)) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sort === "fee") {
        return Number(a.secondOpinionFee) - Number(b.secondOpinionFee);
      }
      if (sort === "turnaround") {
        return (
          (a.secondOpinionTurnaroundDays ?? 99) - (b.secondOpinionTurnaroundDays ?? 99)
        );
      }
      const sa = a.slotsFree != null ? a.slotsFree : -1;
      const sb = b.slotsFree != null ? b.slotsFree : -1;
      return sb - sa;
    });
    return list;
  }, [experts, q, specFilter, langFilter, sort]);

  const confirmAssign = async () => {
    if (!assignCaseId || !pickId) return;
    setAssignBusy(true);
    setError("");
    try {
      await http.post(
        `/api/v1/admin/second-opinion/cases/${assignCaseId}/assign`,
        { expertId: pickId },
        { suppressInlineErrorSwal: true }
      );
      history.push(getPublicUrl(`second-opinion/cases/${assignCaseId}`));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Assign failed");
    } finally {
      setAssignBusy(false);
    }
  };

  return (
    <>
      <Head title="Second opinion experts" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Second opinion — experts</BlockTitle>
              <BlockDes>
                <p>Practitioners who offer second opinions. Assign case files from here.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="light" size="sm" onClick={() => load()} disabled={loading}>
                <Icon name="reload" className="me-1" />
                Refresh
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {error ? (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {assignCaseId ? (
          <Alert color="info" className="mb-3">
            Assigning case <code>{assignCaseId.slice(-8)}</code> — pick an expert below.
          </Alert>
        ) : null}

        <Block>
          <div className="d-flex flex-wrap flex-g-2 mb-3">
            <Input
              style={{ maxWidth: 240 }}
              placeholder="Search a practitioner"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Input
              type="select"
              style={{ maxWidth: 180 }}
              value={specFilter}
              onChange={(e) => setSpecFilter(e.target.value)}
            >
              <option value="">All specialties</option>
              {specialties.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              style={{ maxWidth: 160 }}
              value={langFilter}
              onChange={(e) => setLangFilter(e.target.value)}
            >
              <option value="">All languages</option>
              {languages.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              style={{ maxWidth: 180 }}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="availability">Sort · availability</option>
              <option value="fee">Sort · fee</option>
              <option value="turnaround">Sort · turnaround</option>
            </Input>
          </div>

          <div className="card card-bordered">
            <div className="card-inner">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-soft mb-0">
                  No experts with a fee set. Practitioners enable the service in their settings.
                </p>
              ) : (
                filtered.map((row) => {
                  const langs = langCodes(row);
                  const commit = row.secondOpinionTurnaroundDays;
                  const avg = row.avgTurnaroundDays;
                  const slow = avg != null && commit != null && avg > commit;
                  const slots = row.slotsFree;
                  const free = slots != null && slots > 0;
                  return (
                    <div
                      key={String(row._id)}
                      className="d-flex flex-wrap align-items-start justify-content-between py-3 border-bottom"
                    >
                      <div className="d-flex align-items-start min-w-0 me-3 mb-2 mb-md-0">
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 me-3"
                          style={{
                            width: 40,
                            height: 40,
                            background: "#e5e7eb",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {initials(row.displayName)}
                        </span>
                        <div className="min-w-0">
                          <div className="fw-bold">{row.displayName || "—"}</div>
                          <div className="small text-soft">
                            {specialtyLabel(row)}
                            {langs.length ? ` · ${langs.join(" / ")}` : ""}
                          </div>
                          <div className="small mt-1">
                            {commit != null ? `Commits to ${commit} days` : "No turnaround set"}
                            {avg != null ? (
                              <>
                                {" · "}
                                <span style={slow ? { color: "#92400e", fontWeight: 600 } : undefined}>
                                  avg {avg} days
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center flex-shrink-0 flex-wrap">
                        <strong className="me-2">
                          {formatMoney(row.secondOpinionFee, row.secondOpinionFeeCurrency)}
                        </strong>
                        <span className="text-soft me-2" aria-hidden="true">
                          ·
                        </span>
                        <span
                          className="small me-3"
                          style={{ color: free ? "#059669" : "#6b7280" }}
                        >
                          {slots == null
                            ? "—"
                            : free
                              ? `${slots} slot${slots === 1 ? "" : "s"} free`
                              : "No slots free"}
                        </span>
                        {assignCaseId ? (
                          <Button
                            color="primary"
                            size="sm"
                            disabled={assignBusy || !free}
                            onClick={() => setPickId(String(row._id))}
                          >
                            Assign
                          </Button>
                        ) : (
                          <Button color="light" size="sm" disabled>
                            View
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Block>
      </Content>

      <Modal isOpen={!!pickId} toggle={() => !assignBusy && setPickId("")}>
        <ModalHeader toggle={() => !assignBusy && setPickId("")}>Confirm assignment</ModalHeader>
        <ModalBody>
          <p className="text-soft small mb-0">
            The expert&apos;s current fee will be frozen on this case
            {pickId
              ? (() => {
                  const row = experts.find((e) => String(e._id) === String(pickId));
                  if (!row) return ".";
                  return (
                    <>
                      :{" "}
                      <strong>
                        {formatMoney(row.secondOpinionFee, row.secondOpinionFeeCurrency)}
                      </strong>
                      .
                    </>
                  );
                })()
              : "."}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={assignBusy} onClick={() => setPickId("")}>
            Cancel
          </Button>
          <Button color="primary" disabled={assignBusy} onClick={confirmAssign}>
            {assignBusy ? "Assigning…" : "Assign and freeze fee"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SoExperts;
